import * as React from "react";
import { Label, Pie, PieChart } from "recharts";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../../components/ui/chart";
import { cn } from "../../../../lib/utils";
import { MdInfo } from "react-icons/md";
import ProgressBar from "../../../../components/ProgressBar";
import { useGetRiskScore } from "../../../../helpers/queries/insights/useGetInsights";
import { useNavigate } from "react-router-dom";

export function SeverityChart(severityChartData) {
  const data = severityChartData.severityChartData;
  const generateChartConfig = (data) => {
    const totalItems = data?.length || 1;

    return data?.reduce((config, item, index) => {
      // Distribute hues evenly across the color wheel (0-360°)
      const hue = (360 / totalItems) * index;
      const saturation = 70; // Keep saturation at 70% for vibrancy
      const lightness = 50; // Keep a balanced lightness

      const colorShade = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

      config[item.level] = {
        label: item.level,
        color: colorShade,
      };

      return config;
    }, {});
  };

  const chartConfig = generateChartConfig(data);

  const generateChartData = (data) => {
    const totalItems = data?.length || 1;

    return data?.map((item, index) => {
      // Distribute hues evenly across the color wheel
      const hue = (360 / totalItems) * index;
      const saturation = 70;
      const lightness = 50;

      const colorShade = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

      return {
        level: item.level,
        count: item.count,
        fill: colorShade,
      };
    });
  };

  const chartData = generateChartData(data);

  const emptyChartData = [
    {
      level: "No Data",
      count: 1, // small non-zero value just to show a placeholder
      fill: "#E0E0E0", // light gray
    },
  ];

  const totalCount = React.useMemo(() => {
    return chartData.reduce((acc, curr) => acc + curr.count, 0);
  }, [chartData]);

  const { data: risk } = useGetRiskScore();
  const riskScore = Math.round((risk?.score / 100) * 20) || 0;
  const navigate = useNavigate();
  const handlePieClick = (data) => {
    const severity = data?.name; // Reformat to match URL parameter
    navigate("/dashboard/insights", { state: { severityLevel: severity } });
    // navigate(
    //   `/dashboard/database-activity-logs?filter_interest_reason=failed_login&filter_account_used=${accountUsed}&filter_executed_at_start=${formatTimestamp(
    //     getLast24Hours(new Date())
    //   )}&filter_executed_at_end=${formatTimestamp(new Date())}`
    // );

    console.log(data);
  };

  return (
    <Card className="flex flex-col p-0 h-full">
      <CardHeader className="pb-0">
        <CardTitle className="text-base font-semibold text-left text-[#293056]">
          Vulnerabilities Issues by Severity
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-0 p-0 flex flex-col justify-between h-full">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square max-h-[250px] w-full"
        >
          <PieChart>
            {totalCount > 0 ? (
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
            ) : null}

            <Pie
              data={totalCount > 0 ? chartData : emptyChartData}
              dataKey="count"
              nameKey="level"
              innerRadius={70}
              cornerRadius={5}
              strokeWidth={5}
              onClick={handlePieClick}
              startAngle={180}
              endAngle={totalCount > 0 ? 7 : 0}
              paddingAngle={2}
            >
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 24}
                          className="fill-muted-foreground"
                        >
                          Issues Detected
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className={cn(
                            "fill-foreground text-2xl font-bold",
                            totalCount >= 100000 && " text-xl"
                          )}
                        >
                          {totalCount.toLocaleString() ?? 0}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>

        {/* Legends */}
        <div className="flex justify-center mt-[-80px] mb-4">
          {chartData.map((entry, index) => (
            <div
              key={index}
              className="flex  flex-col items-center gap-2 border-r border-[#cfcfd2] border-dashed last:border-r-0 md:px-4 px-2"
            >
              <span className="text-sm text-gray-600">{entry.level}</span>
              <span>{entry.count}</span>
              <div
                style={{
                  backgroundColor: entry.fill,
                  width: "40px",
                  height: "10px",
                  // borderRadius: "50%",
                }}
              ></div>
            </div>
          ))}
        </div>

        {/* Risk score */}

        <div className=" bg-neutral3 rounded-b-lg p-6  flex flex-col justify-center">
          <div className="border border-[#E8E8EA] rounded-lg bg-white md:p-6 p-3">
            <p className="flex gap-2 items-center text-neutral1000 font-medium mb-2">
              Your risk score is {risk?.score || 0}/100{" "}
              <MdInfo className="text-[#B7B7B9]" />
            </p>
            <p className="text-neutral-600 text-xs mb-6">
              Several vulnerabilities detected from scans.
            </p>
            <ProgressBar filledBars={riskScore} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
